import { toast, ToastItem } from "react-toastify";
import { ServiceBase } from "../ServiceBase";
import { IToasterService, IToastDefinition, IStandardToastDefinition } from "./toaster.service.interface";
import { IServiceFactoryExtended } from "../service-factory-extended.interface";
import { action, makeObservable, observable, runInAction } from "mobx";
import { ToastQueue } from "./toasts-queue";
import { StandardToastContent } from "../../components/toaster/toasts-content/standard-toast.component";
import { ToastHandler } from "./toast-handler";

export class ToasterService extends ServiceBase implements IToasterService {
    private _toastQueue: ToastQueue = new ToastQueue();

    private _pendingToasts: ToastHandler[] = [];

    /** use observable instead of observable.ref because the ref remains the same, instead i push and splice data from this array */
    @observable
    private _activeToasts: ToastHandler[] = [];

    private get _allToasts(): ToastHandler[] {
        return [
            ...this._pendingToasts,
            ...this._activeToasts
        ];
    }

    @observable.ref
    private isAppVisible = true;

    constructor(services: IServiceFactoryExtended) {
        super(services);

        // I want to clear the queue of toasts when the authorized state is changed but not on initial load so no fireImmediately
        this.reaction(() => this.services.user.isAuthorized, () => {
            this._toastQueue.clearQueue();
        });

        this.reaction(() => this.services.user.isAuthorized, (isAuthorized) => {
            if (isAuthorized) {
                // this call will have an actual efect only at app init, otherwise the queue is cleared at authorized state change
                this._toastQueue.processQueue();
            } else {
                this.removeAllToasts();
            }
        }, {
            fireImmediately: true
        });



        this.services.sdk.events.onAppPaused(() => {
            this._setIsAppVisible(false);
        });
        this.services.sdk.events.onAppResumed(() => {
            this._setIsAppVisible(true);
            this._toastQueue.processQueue();
        });

        toast.onChange((payload: ToastItem<ToastHandler>) => {
            switch (payload.status) {
                case "added":
                    // new toast added
                    runInAction(() => {
                        const pendingToastIndex = this._pendingToasts.findIndex(t => t.toastId === payload.data.toastId);
                        if (pendingToastIndex >= 0) {
                            this._pendingToasts.splice(pendingToastIndex, 1);
                        }
                        this._activeToasts.push(payload.data);
                    });
                    payload.data.onToastShown();
                    break;
                case "updated":
                    // toast updated
                    break;
                case "removed":
                    // toast has been removed
                    runInAction(() => {
                        const idx = this._activeToasts.findIndex((handler) => handler.toastId === payload.data.toastId);
                        if (idx >= 0) {
                            this._activeToasts.splice(idx, 1);
                        }
                    });
                    payload.data.onToastClosed();
                    break;
            }
        });
        makeObservable(this);
    }

    get hasOpenToasters(): boolean {
        return this._activeToasts.length > 0;
    }

    @action
    private _setIsAppVisible(isAppVisible: boolean): void {
        this.isAppVisible = isAppVisible;
    }

    showAckStandardToast(def: IStandardToastDefinition) {
        return this.showStandardToast({
            ...def,
            contentProps: {
                ...def.contentProps,
                button: def.contentProps.button ? def.contentProps.button : {
                    text: this.services.translation.translate('OK'),
                    onClick: (closeToast) => {
                        closeToast();
                    }
                }
            }
        });
    }

    showStandardToast(def: IStandardToastDefinition) {
        const { contentProps, ...partiaToastDef } = def;
        return this.showCustomToast({
            ...partiaToastDef,
            renderContent: (handler) => <StandardToastContent {...def.contentProps} handler={handler} />
        });
    }


    showCustomToast(options: IToastDefinition) {

        if (this._allToasts.find(t => t.toastId === options.id)) {
            return;
        }

        const toast = new ToastHandler(this.services, options);
        this._pendingToasts.push(toast);

        if (options.toastOptions?.timeout && options.toastOptions?.timeout.totalMilliseconds > 0 && !this.isAppVisible) {
            this._toastQueue.addToQueue(toast);
            // this.logger.debug('TOASTER :: SAVED TO LOCAL STORAGE ', toasterParams);
            return;
        }
        toast.show();
        return toast;
    }


    removeAllToasts() {
        toast.dismiss();
        this._pendingToasts.forEach(t => t.onToastClosed());
        this._pendingToasts = [];
    }
}